// @flow
import api from '../api/catalogAd';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

const REDUX_STORE_TIME = ms('3m');

/* Action Types */
export const LOAD_CATALOG_AD_FAIL = 'la/domain/catalogAds/LOAD_FAIL';
export const LOAD_CATALOG_AD_REQUEST = 'la/domain/catalogAds/LOAD_REQUEST';
export const LOAD_CATALOG_AD_SUCCESS = 'la/domain/catalogAds/LOAD_SUCCESS';

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    byKeyword: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let existingByKeywords;
    let loaded;
    let loading;

    switch (action.type) {
        case LOAD_CATALOG_AD_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.keywords || action.meta.categoryIds]),
            };
        case LOAD_CATALOG_AD_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_CATALOG_AD_SUCCESS:
            const { categoryIds, keywords } = action.meta;
            existing = cloneDeep(state.byId);
            existingByKeywords = cloneDeep(state.byKeyword);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            loaded[keywords || categoryIds] = action.meta.actionTime;
            loading = difference(loading, [keywords || categoryIds]);
            if (keywords) {
                existingByKeywords[keywords] = action.payload.ads[0];
            } else {
                existing[categoryIds] = action.payload.ads[0];
            }

            return {
                ...state,
                byId: existing,
                byKeyword: existingByKeywords,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.catalogAd;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);
const byKeywordSelector = createSelector(stateSelector, state => state.byKeyword);
const loadedSelector = createSelector(stateSelector, state => state.loaded);
const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getAdById = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});
export const getAdByKeyword = createSelector(
    [byKeywordSelector, idSelector],
    (byKeyword, keywords) => byKeyword[keywords] || {}
);

export const getAd = (state: any, categoryIds: any, keywords: any, pageName: any) => {
    if (keywords) return getAdByKeyword(state, `${pageName}-${keywords}`);
    else if (categoryIds) return getAdById(state, categoryIds);
    else return {};
};

export const getLoadTimeForCatalogAd = (state: any, categoryIds: any, keywords: any, pageName: any) => {
    let selector = (keywords && `${pageName}-${keywords}`) || categoryIds;
    return createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0)(state, selector);
};

export const isCatalogAdLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));
const loadCatalogAd = (categoryIds, keywords, pageName) => async (dispatch, getState) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        dispatch({
            // $FlowFixMe
            payload: (keywords && `${pageName}-${keywords}`) || categoryIds,
            type: LOAD_CATALOG_AD_REQUEST,
        });
        const response = await api.fetchCatalogAd({ categoryIds, deployment, keywords, pageName });
        dispatch({
            // $FlowFixMe
            meta: { actionTime: Date.now(), categoryIds, keywords: keywords && `${pageName}-${keywords}` },
            payload: response.data,
            type: LOAD_CATALOG_AD_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            // $FlowFixMe
            meta: { categoryIds, keywords: keywords && `${pageName}-${keywords}` },
            payload: error,
            type: LOAD_CATALOG_AD_FAIL,
        });
    }
};

const shouldFetchCatalogAd = (state, categoryIds, keywords, pageName) => {
    if (!categoryIds && !keywords) return false;
    const item = getAd(state, categoryIds, keywords, pageName);
    if (item) {
        const loaded = getLoadTimeForCatalogAd(state, categoryIds, keywords, pageName);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    const loading = isCatalogAdLoading(state, categoryIds);
    return !loading;
};

type FetchCatalogAdIfNeededParams = {
    categoryIds: any,
    keywords?: any,
    pageName?: any,
};

export const fetchCatalogAdIfNeeded = ({ categoryIds, keywords, pageName }: FetchCatalogAdIfNeededParams) => async (
    dispatch: Function,
    getState: Function
) => {
    if (shouldFetchCatalogAd(getState(), categoryIds, keywords, pageName)) {
        return dispatch(loadCatalogAd(categoryIds, keywords, pageName));
    }
    return Promise.resolve();
};
